(() => {
  const gridSelector = '#MainContent #product-grid.product-grid, #MainContent .depict-grid';
  const activeClass = 'yazuri-infinite-scroll-active';
  let activeState;
  let syncFrame;

  function normalizeUrl(href) {
    if (!href) return null;
    const url = new URL(href, location.href);
    if (url.origin !== location.origin) return null;
    url.hash = '';
    return url.href;
  }

  function nextPageUrl(root) {
    return normalizeUrl(root?.querySelector('.pagination-wrapper a.pagination__item--prev[href]')?.getAttribute('href'));
  }

  function productKey(card) {
    const depictId = card.querySelector('[data-depict-product-id]')?.getAttribute('data-depict-product-id');
    if (depictId) return `id:${depictId}`;
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    return `path:${new URL(link.href, location.href).pathname}`;
  }

  function productCards(grid) {
    return [...grid.children].filter(child => child.matches('.grid__item') && productKey(child));
  }

  function destroy(state, showPagination = false) {
    state.observer.disconnect();
    state.abortController?.abort();
    state.sentinel.remove();
    state.loader.remove();
    if (showPagination) state.container.classList.remove(activeClass);
  }

  async function loadNext(state) {
    if (activeState !== state || state.isLoading || !state.hasMore || !state.nextUrl) return;
    const url = state.nextUrl;
    if (state.loadedUrls.has(url)) {
      finish(state);
      return;
    }

    state.isLoading = true;
    state.loader.hidden = false;
    state.grid.setAttribute('aria-busy', 'true');
    state.abortController = new AbortController();

    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: state.abortController.signal });
      if (!response.ok) throw new Error(`Collection page ${response.status}`);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = page.querySelector(gridSelector);
      if (!nextGrid) throw new Error('Collection grid missing');
      const cards = productCards(nextGrid);
      if (!cards.length) throw new Error('Collection page has no product cards');
      if (activeState !== state || !state.grid.isConnected) return;

      const fragment = document.createDocumentFragment();
      let appended = 0;
      for (const card of cards) {
        const key = productKey(card);
        if (state.productKeys.has(key)) continue;
        state.productKeys.add(key);
        fragment.appendChild(document.importNode(card, true));
        appended++;
      }
      if (!appended) throw new Error('Collection page contains only duplicate products');

      state.grid.appendChild(fragment);
      state.loadedUrls.add(url);
      state.nextUrl = nextPageUrl(page);
      state.hasMore = Boolean(state.nextUrl) && !state.loadedUrls.has(state.nextUrl);
      document.dispatchEvent(new CustomEvent('yazuri:products-appended', {
        detail: { grid: state.grid, count: appended }
      }));
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (activeState === state && !state.hasMore) finish(state);
    } catch (error) {
      if (error.name !== 'AbortError' && activeState === state) {
        state.hasMore = false;
        destroy(state, true);
      }
    } finally {
      state.grid.removeAttribute('aria-busy');
      state.loader.hidden = true;
      state.isLoading = false;
      state.abortController = null;
    }
  }

  function finish(state) {
    state.hasMore = false;
    state.observer.disconnect();
    state.sentinel.remove();
    state.loader.remove();
  }

  function mount(grid) {
    const container = grid.closest('#ProductGridContainer');
    const nextUrl = nextPageUrl(container);
    if (!container || !nextUrl) return;

    const sentinel = document.createElement('div');
    sentinel.className = 'yazuri-infinite-sentinel';

    const loader = document.createElement('div');
    loader.className = 'yazuri-infinite-loader';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = '<span class="yazuri-infinite-loader__spinner" aria-hidden="true"></span><span>Chargement...</span>';
    loader.hidden = true;

    grid.insertAdjacentElement('afterend', sentinel);
    sentinel.insertAdjacentElement('afterend', loader);
    container.classList.add(activeClass);

    const state = {
      grid,
      container,
      sentinel,
      loader,
      isLoading: false,
      nextUrl,
      loadedUrls: new Set([normalizeUrl(location.href)]),
      productKeys: new Set(productCards(grid).map(productKey)),
      hasMore: true,
      abortController: null,
      observer: null
    };
    state.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadNext(state);
    }, { rootMargin: '0px 0px 500px 0px', threshold: 0 });
    activeState = state;
    state.observer.observe(sentinel);
  }

  function sync() {
    syncFrame = undefined;
    const grid = document.querySelector(gridSelector);
    if (grid === activeState?.grid) return;
    if (activeState) {
      destroy(activeState, true);
      activeState = undefined;
    }
    if (grid) mount(grid);
  }

  function scheduleSync() {
    if (!syncFrame) syncFrame = requestAnimationFrame(sync);
  }

  function start() {
    sync();
    new MutationObserver(scheduleSync).observe(document.body, { childList: true, subtree: true });
    addEventListener('popstate', scheduleSync);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
